import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth-service';
import { moduleService } from '../services/module-service';
import { AppHeader } from '../components/AppHeader';
import { InlineList, InlineListRow } from '../components/InlineList';
import type { Module } from '../types/module';
import type { User } from '../types/auth';
import './Modules.css';
import '../styles/common.css';

function Modules() {
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const handleModuleClick = (moduleId: string, classId?: string) => {
    navigate(classId ? `/subjects?moduleId=${moduleId}&classId=${classId}` : `/subjects?moduleId=${moduleId}`);
  };

  const handleLogout = async () => {
    authService.logout();
    navigate('/');
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        if (!authService.isAuthenticated()) {
          navigate('/');
          return;
        }

        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);

        const userModules = await moduleService.getModules();

        if (userModules.length === 0) {
          setError('You do not have access to any valid modules. Please contact your administrator.');
          return;
        }

        setModules(userModules);
        setError(null);
      } catch (err: any) {
        if (err.response?.status === 401) {
          authService.logout();
          navigate('/');
        } else {
          setError('Could not load modules. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="modules-container">
        <AppHeader
          user={user}
          title="KI Expert"
          subtitle="Select a module from the list:"
        />
        <div className="modules-loading">Loading modules...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modules-container">
        <AppHeader
          user={user}
          title="KI Expert"
          subtitle="Select a module from the list:"
        />
        <div className="modules-error">{error}</div>
        <button onClick={handleLogout} className="modules-logout-button">
          Back to login
        </button>
      </div>
    );
  }

  return (
    <div className="modules-container">
      <AppHeader
        user={user}
        title="KI Expert"
        subtitle="Select a module from the list:"
      />

      <main className="modules-main">
        <div className="navigation-bar">
          <h2 className="navigation-bar-title">Module</h2>
        </div>
        <InlineList>
          {modules.map((module, index) => (
            <InlineListRow
              key={`${module.id}-${module.classId ?? index}`}
              onClick={() => handleModuleClick(module.id, module.classId)}
            >
              <div className="module-row-content">
                <div className="module-row-info">
                  <h3 className="module-row-title">{module.className ? `${module.name} - ${module.className}` : module.name}</h3>
                </div>
              </div>
            </InlineListRow>
          ))}
        </InlineList>
      </main>
    </div>
  );
}

export default Modules;
